import React, { useMemo } from 'react';
import { Info } from 'lucide-react';

import {
  AlertBanner,
  AlertBannerIcon,
  AlertBannerDescription
} from '@epoch-ui/components/AlertBanner';
import { Button } from '@epoch-ui/components/Button';

import { useResendFeedbackFormModal } from '../../../hooks/eventEditorState';

export const FeedbackWarningType = {
  RESEND_FEEDBACK: 'resend-feedback',
  UPDATE_SETTINGS: 'update-settings'
} as const;

interface FeedbackWarningBannerProps {
  type: typeof FeedbackWarningType[keyof typeof FeedbackWarningType];
  onSettings: () => void;
  onResendSuccess?: () => void;
  className?: string;
}

export const FeedbackWarningBanner: React.FC<FeedbackWarningBannerProps> = ({
  type,
  onSettings,
  onResendSuccess,
  className = ''
}) => {
  const resendFeedbackFormModal = useResendFeedbackFormModal();

  const content = useMemo(() => {
    switch (type) {
      case FeedbackWarningType.RESEND_FEEDBACK:
        return (
          <>
            The feedback form has already been sent to guests.{' '}
            <Button
              className="tw-h-auto tw-w-auto tw-p-0 tw-font-normal"
              type="button"
              variant="link"
              onClick={() => resendFeedbackFormModal.open({ onSuccess: onResendSuccess })}
            >
              Resend
            </Button>{' '}
            the feedback form to make sure the updated questionnaire is sent to guests.
          </>
        );
      case FeedbackWarningType.UPDATE_SETTINGS:
        return (
          <>
            Update feedback form{' '}
            <Button
              className="tw-h-auto tw-w-auto tw-p-0 tw-font-normal"
              type="button"
              variant="link"
              onClick={onSettings}
            >
              settings
            </Button>{' '}
            to schedule when the updated questionnaire should be sent to guests.
          </>
        );
      default:
        return null;
    }
  }, [type, onSettings, onResendSuccess, resendFeedbackFormModal]);

  return (
    <AlertBanner className={className} variant="warning">
      <AlertBannerIcon>
        <Info size={16} />
      </AlertBannerIcon>
      <AlertBannerDescription>{content}</AlertBannerDescription>
    </AlertBanner>
  );
};
